"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight, Users, Briefcase, Star } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
const vehicles = [{
  name: "Cadillac CT6 - Luxury Sedan",
  image: "https://present-gnat.10web.cloud/wp-content/uploads/2025/11/Stellar-Black-Metallic-e1762639365321.jpg",
  passengers: "3",
  luggage: "3 Bags",
  features: "Premium leather, Wi-Fi, climate control"
}, {
  name: "Chevrolet Suburban —Executive SUV",
  image: "https://present-gnat.10web.cloud/wp-content/uploads/2025/11/image.jpg",
  passengers: "7 Passengers",
  luggage: "6 Bags",
  features: "Spacious interior, entertainment system, panoramic roof"
}, {
  name: "Cadillac Escalade — Luxury SUV",
  image: "https://present-gnat.10web.cloud/wp-content/uploads/2025/11/2025_cadillac_escalade-esv_sport_032_gba-e1752202740841.png",
  passengers: "6",
  luggage: "6 Bags",
  features: "Premium comfort, advanced safety, luxury amenities"
}, {
  name: "Volvo S90 - Luxury Sedan",
  image: "https://present-gnat.10web.cloud/wp-content/uploads/2025/11/2021-volvo-s90-t6-awd-inscription-angular-front-exterior-view_100846451_l-e1752202292144.jpg",
  passengers: "3",
  luggage: "3 Bags",
  features: "Executive seating, privacy glass, premium sound"
}];
export default function OurLuxuryFleet() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  return <section ref={ref} className="bg-background py-16 md:py-24 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className={`text-center mb-12 transition-all duration-[var(--duration-slow)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            Our Luxury Fleet
          </h2>
          <p className="text-lg text-muted max-w-2xl mx-auto">
            Experience premium comfort and style with our carefully curated selection of luxury vehicles
          </p>
        </div>

        <div className={`relative transition-all duration-[var(--duration-slow)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-2 md:-ml-4">
              {vehicles.map((vehicle, i) => <CarouselItem key={i} data-index={i} className="pl-2 md:pl-4 basis-full md:basis-1/2 lg:basis-1/3">
                  <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <Card className="overflow-hidden cursor-pointer group shadow-soft hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] bg-secondary" onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)}>
                        <div className="relative overflow-hidden aspect-[4/3]">
                          <img src={vehicle.image} alt={vehicle.name} className={`w-full h-full object-contain transition-transform duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:scale-103`} />
                          <div className={`absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${hoveredIndex === i ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}>
                            <div className="absolute bottom-0 left-0 right-0 p-6 text-foreground">
                              <div className="flex items-center gap-4 mb-2">
                                <div className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  <span className="text-sm font-medium">{vehicle.passengers} Passengers</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Briefcase className="w-4 h-4" />
                                  <span className="text-sm font-medium">{vehicle.luggage} Bags</span>
                                </div>
                              </div>
                              <p className="text-sm text-foreground/90">
                                {vehicle.features}
                              </p>
                            </div>
                          </div>
                        </div>
                        <CardContent className="p-6">
                          <h3 className="text-xl font-display font-semibold text-foreground-secondary mb-2">
                            {vehicle.name}
                          </h3>
                          <div className="flex items-center gap-1 text-accent">
                            <Star className="w-4 h-4 fill-current" />
                            <Star className="w-4 h-4 fill-current" />
                            <Star className="w-4 h-4 fill-current" />
                            <Star className="w-4 h-4 fill-current" />
                            <Star className="w-4 h-4 fill-current" />
                          </div>
                        </CardContent>
                      </Card>
                    </HoverCardTrigger>
                    <HoverCardContent align="center" className="w-80 glass-effect">
                      <div className="space-y-2">
                        <h4 className="font-display font-semibold text-foreground">
                          {vehicle.name}
                        </h4>
                        <p className="text-sm text-muted">
                          Premium features designed for your comfort and convenience during every journey.
                        </p>
                        <div className="flex gap-4 pt-2">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-accent" />
                            <span className="text-sm text-foreground">{vehicle.passengers}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-accent" />
                            <span className="text-sm text-foreground">{vehicle.luggage}</span>
                          </div>
                        </div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="bg-secondary hover:bg-accent hover:text-foreground-accent shadow-medium focus-ring -left-12" />
              <CarouselNext className="bg-secondary hover:bg-accent hover:text-foreground-accent shadow-medium focus-ring -right-12" />
            </div>
          </Carousel>

          <div className="flex justify-center gap-2 mt-8 md:hidden">
            <Button variant="outline" size="icon" aria-label="Previous vehicle" className="rounded-full bg-secondary text-foreground-secondary hover:bg-accent hover:text-foreground-accent size-10 shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus-ring">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button variant="outline" size="icon" aria-label="Next vehicle" className="rounded-full bg-secondary text-foreground-secondary hover:bg-accent hover:text-foreground-accent size-10 shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] focus-ring">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}